/** Same-origin private API; principal and session belong exclusively to Platform. */
export class ContenedoresApiError extends Error {
  constructor(readonly status:number, readonly code:string, message:string) {
    super(message); this.name="ContenedoresApiError";
  }
}
const ORG=/^cd_org_[a-f0-9]{32}$/;
const SUFFIX=/^(?:me|metrics|assets|incidents|reservations|routes|tasks|customers|quotes|orders|leads|knowledge|knowledge\/index|knowledge\/search|knowledge\/pdf|knowledge\/review|knowledge\/preview|finance|audit|twin|twin\/simulate|agent\/workflows|agent\/workflows\/[a-f0-9-]{36}\/approve|copilot|copilot\/execute|org\/onboarding|assets\/[A-Za-z0-9_-]{1,80}\/status|quotes\/[A-Za-z0-9-]{1,80}\/accept|tasks\/[A-Za-z0-9-]{1,80}\/(?:assign|status)|routes\/compute)$/;
export function contenedoresEndpoint(org:string,suffix:string):string {
  if(!ORG.test(org)||!SUFFIX.test(suffix)||suffix.includes(".."))throw Error("invalid_contenedores_route");
  return `/api/v1/contenedores/organizations/${org}/${suffix}`;
}
export interface ContenedoresClientOptions {
  fetcher?: typeof fetch;
  csrf:()=>Promise<string>;
  onExpired:()=>void;
}
export function createContenedoresClient(org:string,{fetcher=fetch,csrf,onExpired}:ContenedoresClientOptions){
  async function request<T>(suffix:string,method:"GET"|"POST"|"PATCH"="GET",payload?:unknown):Promise<T>{
    const path=contenedoresEndpoint(org,suffix);
    const headers=new Headers({accept:"application/json"});
    if(method!=="GET"){
      const token=await csrf();
      if(!token||token.length>512)throw new ContenedoresApiError(403,"csrf_unavailable","No se pudo obtener una autorización actualizada.");
      headers.set("Content-Type","application/json");
      headers.set("X-CSRF-Token",token);
    }
    let response:Response;
    try{response=await fetcher(path,{method,headers,credentials:"include",cache:"no-store",redirect:"error",...(method==="GET"?{}:{body:JSON.stringify(payload??{})})})}
    catch{throw new ContenedoresApiError(0,"network_unavailable","No fue posible contactar al servidor. Verificá la conexión.")}
    if(response.status===401)onExpired();
    const body=await response.json().catch(()=>null) as Record<string,unknown>|null;
    if(!response.ok){
      const code=typeof body?.code==="string"?body.code:"request_failed";
      const message=typeof body?.error==="string"?body.error:typeof body?.message==="string"?body.message:`Error del servidor (${response.status}).`;
      throw new ContenedoresApiError(response.status,code,message);
    }
    if(body===null||Array.isArray(body)||typeof body!=="object")throw new ContenedoresApiError(502,"invalid_response","El servidor devolvió una respuesta inesperada.");
    return body as T;
  }
  return Object.freeze({
    get:<T>(suffix:string)=>request<T>(suffix),
    post:<T>(suffix:string,payload:unknown)=>request<T>(suffix,"POST",payload),
    patch:<T>(suffix:string,payload:unknown)=>request<T>(suffix,"PATCH",payload),
    uploadPdf:async <T>({file,id,title,kind="document",revision=1}:{file:File;id:string;title:string;kind?:string;revision?:number}):Promise<T>=>{
      if(!/^[A-Za-z0-9_-]{2,96}$/.test(id)||title.trim().length<2||title.length>150||!['document','faq','policy','procedure','contract'].includes(kind)||revision<1||!Number.isSafeInteger(revision)||file.type!=='application/pdf'||file.size<16||file.size>3*1024*1024)
        throw new ContenedoresApiError(400,'OCR_METADATA_INVALID','Seleccioná un PDF válido de hasta 3 MB.');
      const token=await csrf();
      if(!token||token.length>512)throw new ContenedoresApiError(403,'csrf_unavailable','No se pudo autorizar la carga.');
      const headers=new Headers({'Content-Type':'application/pdf','X-CSRF-Token':token,'X-CTN-Doc-Id':id,'X-CTN-Doc-Title':encodeURIComponent(title),'X-CTN-Doc-Kind':kind,'X-CTN-Doc-Revision':String(revision)});
      let response:Response;
      try{response=await fetcher(contenedoresEndpoint(org,'knowledge/pdf'),{method:'POST',body:file,credentials:'include',headers,cache:'no-store',redirect:'error'});}
      catch{throw new ContenedoresApiError(0,'network_unavailable','No fue posible contactar al servidor de documentos.');}
      if(response.status===401)onExpired();
      const result=await response.json().catch(()=>null) as Record<string,unknown>|null;
      if(!response.ok)throw new ContenedoresApiError(response.status,typeof result?.code==='string'?result.code:'OCR_UPLOAD_FAILED',typeof result?.error==='string'?result.error:'No se pudo procesar el PDF.');
      if(!result||typeof result!=='object'||Array.isArray(result))throw new ContenedoresApiError(502,'invalid_response','Respuesta documental inválida.');
      return result as T;
    },

  });
}
